// Bannière douce visible en haut de l'accueil quand l'abonnement (payant) va
// se terminer sous 3 jours suite à annulation. Non intrusif, dismissable.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Clock, X } from "lucide-react";

const DISMISS_KEY = "softlanding.expiration.dismissed.v1";
const THRESHOLD_MS = 3 * 24 * 3600 * 1000;

export default function ExpirationReminder() {
  const { user, entitlement } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const { endsAt } = JSON.parse(raw);
        // On considère le rappel masqué uniquement pour cette échéance précise
        if (endsAt === entitlement?.ends_at) setDismissed(true);
      }
    } catch {}
  }, [entitlement?.ends_at]);

  if (!user) return null;
  if (!entitlement?.premium) return null;
  if (!entitlement?.cancel_at_period_end) return null;
  if (!entitlement?.ends_at) return null;

  const endsAt = new Date(entitlement.ends_at);
  const remaining = endsAt.getTime() - Date.now();
  if (remaining <= 0 || remaining > THRESHOLD_MS) return null;
  if (dismissed) return null;

  const days = Math.max(1, Math.ceil(remaining / (24 * 3600 * 1000)));

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify({ endsAt: entitlement.ends_at }));
    } catch {}
    setDismissed(true);
  }

  return (
    <div
      data-testid="expiration-reminder"
      className="mt-4 rounded-2xl border border-[color:var(--sauge)]/40 bg-[color:var(--sauge)]/10 p-4 flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-xl bg-[color:var(--sauge)]/20 border border-[color:var(--sauge)]/40 flex items-center justify-center flex-shrink-0">
        <Clock size={14} className="text-[color:var(--sauge-light)]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[color:var(--ivoire)]">
          Votre Premium se termine dans <span className="font-medium">{days} jour{days > 1 ? "s" : ""}</span>.
        </p>
        <p className="text-[11px] text-[color:var(--ivoire-muted)] mt-1">
          Après le {endsAt.toLocaleDateString("fr-FR")}, vous retrouverez l'accès gratuit. Vous pouvez reprendre à tout moment.
        </p>
        <Link
          data-testid="expiration-resume-link"
          to="/mon-espace"
          className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--sauge-light)] underline underline-offset-4"
        >
          Reprendre mon abonnement
        </Link>
      </div>
      <button
        data-testid="expiration-dismiss"
        onClick={dismiss}
        aria-label="Masquer"
        className="w-8 h-8 rounded-lg text-[color:var(--ivoire-muted)] hover:text-[color:var(--ivoire)]"
      >
        <X size={14} className="mx-auto" />
      </button>
    </div>
  );
}
